import { AudioLines, AudioWaveform, TriangleAlert, Volume2 } from 'lucide-react';

import type { VadPreset, VadValues } from './vad-config';

import {
  SILERO_VAD_SECTION,
  VOLUME_VAD_SECTION,
  VAD_PRESETS,
  getVadParameter,
  validateVadValues,
} from './vad-config';
import { VadLiveStatus } from './vad-live-status';
import { VadSection } from './vad-section';

interface VadSettingsPanelProps {
  readonly values: VadValues;
  readonly onChange: (values: VadValues) => void;
  readonly presets?: readonly VadPreset[];
}

/**
 * VAD settings panel ("Détection de la voix").
 * Controlled component: persist `values` via `onChange` (store or settings API).
 *
 * Usage:
 *   const [vadValues, setVadValues] = useState(loadVadValues);
 *   <VadSettingsPanel values={vadValues} onChange={setVadValues} />
 */
export function VadSettingsPanel({
  values,
  onChange,
  presets = VAD_PRESETS,
}: VadSettingsPanelProps): React.JSX.Element {
  const errors = validateVadValues(values);
  const threshold =
    values['volume.threshold'] ?? getVadParameter('volume.threshold')?.defaultValue ?? -55;
  // Active preset is derived from the values themselves so the highlight
  // survives reloads without extra persisted state.
  const activePreset =
    presets.find(preset =>
      Object.entries(preset.values).every(([key, value]) => values[key] === value),
    ) ?? null;

  const handleValueChange = (key: string, value: number): void => {
    onChange({ ...values, [key]: value });
  };

  const handleReset = (keys: readonly string[]): void => {
    const next: VadValues = { ...values };
    for (const key of keys) {
      const definition = getVadParameter(key);
      if (definition) {
        next[key] = definition.defaultValue;
      }
    }
    onChange(next);
  };

  const handlePreset = (preset: VadPreset): void => {
    onChange({ ...values, ...preset.values });
  };

  return (
    <div className="flex flex-col gap-4 rounded-2xl border bg-background p-4">
      <header className="flex items-center gap-3">
        <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 text-primary">
          <AudioWaveform className="h-5 w-5" />
        </span>
        <div className="flex flex-col">
          <h2 className="text-base font-semibold text-foreground">Détection de la voix</h2>
          <p className="text-xs text-muted-foreground">
            Deux filtres complémentaires : le volume, puis la voix
          </p>
        </div>
      </header>

      <VadLiveStatus threshold={threshold} />

      <p className="text-xs leading-relaxed text-muted-foreground">
        Choisissez un préréglage selon le lieu, puis ajustez si besoin : touchez − / + pour un
        réglage précis, ou la valeur pour la taper au clavier. Chaque curseur indique aux deux bouts
        ce qui se passe. Les deux voyants ci-dessus confirment que le micro capte votre voix et que
        l'IA la reconnaît.
      </p>

      {presets.length > 0 && (
        <div className="flex flex-col gap-2">
          <div className="flex flex-wrap items-center gap-2">
            <span className="w-full text-xs font-medium text-muted-foreground">Préréglages :</span>
            {presets.map(preset => (
              <button
                key={preset.id}
                type="button"
                onClick={() => handlePreset(preset)}
                aria-pressed={activePreset?.id === preset.id}
                className={`flex h-11 flex-1 items-center justify-center rounded-full border px-4 text-sm transition-colors ${
                  activePreset?.id === preset.id
                    ? 'border-primary bg-primary/10'
                    : 'bg-background hover:bg-muted'
                }`}
              >
                {preset.label}
              </button>
            ))}
          </div>
          {activePreset && (
            <p className="text-xs leading-relaxed text-muted-foreground">
              {activePreset.description}
            </p>
          )}
        </div>
      )}

      {errors.length > 0 && (
        <div
          role="alert"
          className="flex items-start gap-2 rounded-lg border border-destructive/30 bg-destructive/10 p-3"
        >
          <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0 text-destructive" />
          <div className="flex flex-col gap-1">
            {errors.map(error => (
              <p key={error} className="text-xs text-destructive">
                {error}
              </p>
            ))}
          </div>
        </div>
      )}

      <VadSection
        definition={VOLUME_VAD_SECTION}
        values={values}
        onValueChange={handleValueChange}
        onReset={handleReset}
        icon={<Volume2 className="h-4 w-4" />}
      />
      <VadSection
        definition={SILERO_VAD_SECTION}
        values={values}
        onValueChange={handleValueChange}
        onReset={handleReset}
        icon={<AudioLines className="h-4 w-4" />}
      />
    </div>
  );
}
